import React, { useState, useEffect } from 'react';
import { Target, Users, MessageCircle, Hash, Sparkles, ChevronDown, Check, Zap, BookOpen, Briefcase, Smile, Trash2, Globe, Plus, X, ExternalLink } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Textarea } from '../ui/Textarea';
import { Loading } from '../ui/Loading';
import { webhookService, projectService } from '../../services/api';
import { StyleAnalysisCard } from '../ui/StyleAnalysisCard';
import { generateSessionId } from '../../utils/helpers';

// Función para extraer subtemas del texto (copiada de ChatBriefing)
const extractSubtopics = (text: string): string[] => {
  const subtopics: string[] = [];

  // PRIORIDAD 1: Buscar formato "SUBTEMA 1:", "SUBTEMA 2:", etc.
  const subtemaPattern = /SUBTEMA\s*\d+\s*[:：]\s*([^\n]+)/gi;
  const subtemaMatches = text.matchAll(subtemaPattern);

  for (const match of subtemaMatches) {
    let topic = match[1]?.trim();
    if (topic && topic.length > 10 && topic.length < 200) {
      subtopics.push(topic);
    }
  }

  if (subtopics.length >= 3) {
    return subtopics.slice(0, 4);
  }

  // PRIORIDAD 2: Buscar formato con números "1.", "2.", etc.
  const numberedPattern = /^\s*\d+\.\s+([^\n]+)/gm;
  const numberedMatches = text.matchAll(numberedPattern);

  for (const match of numberedMatches) {
    let topic = match[1]?.trim();
    if (topic && topic.length > 10 && topic.length < 200 &&
      !topic.includes('RESUMEN') &&
      !topic.includes('palabras') &&
      !topic.includes('📋')) {
      if (!subtopics.includes(topic)) {
        subtopics.push(topic);
      }
    }
  }

  if (subtopics.length >= 3) {
    return subtopics.slice(0, 4);
  }

  return subtopics.slice(0, 4).filter(t => t.length > 10);
};

// Componente local removido en favor de '../ui/StyleAnalysisCard'

interface TraditionalFormProps {
  onComplete: (data: any) => void;
  projectId?: string;
  onBack?: () => void;
  sessionId?: string;
  articleType?: string; // Para determinar si usar OpenAI o n8n en análisis de estilo
}

export const TraditionalForm: React.FC<TraditionalFormProps> = ({ onComplete, projectId, sessionId: propSessionId, articleType }) => {
  const [formData, setFormData] = useState({
    topic: '',
    targetKeyword: '',
    secondaryKeywords: '',
    audience: '',
    tone: 'profesional',
    wordCount: 1500,
    objectives: '',
    additionalRequirements: '',
    // CTA fields
    ctaText: '',
    ctaUrl: '',
    // Title mode: true = strict title, false = use as topic
    isTitleStrict: true, // Default: título estricto
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isGeneratingSubtopics, setIsGeneratingSubtopics] = useState(false);

  // WordPress Style State
  const [styleMode, setStyleMode] = useState<'preset' | 'wordpress' | 'external'>('preset');
  const [wpPosts, setWpPosts] = useState<any[]>([]);
  const [isLoadingWpPosts, setIsLoadingWpPosts] = useState(false);
  const [selectedWpPosts, setSelectedWpPosts] = useState<string[]>([]);
  const [expandedPostId, setExpandedPostId] = useState<string | null>(null);
  const [analysisResultDisplay, setAnalysisResultDisplay] = useState<string | null>(null);

  // External URLs Style State
  const [externalUrls, setExternalUrls] = useState<string[]>(['']);
  const [includeStructure, setIncludeStructure] = useState(false);
  const [isAnalyzingExternal, setIsAnalyzingExternal] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validación
    const newErrors: Record<string, string> = {};
    if (!formData.topic.trim()) newErrors.topic = 'El tema es requerido';
    if (!formData.targetKeyword.trim()) newErrors.targetKeyword = 'La palabra clave es requerida';
    if (!formData.audience.trim()) newErrors.audience = 'La audiencia es requerida';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsGeneratingSubtopics(true);

    try {
      // Construir un prompt descriptivo para el webhook del chat
      let prompt = `Necesito crear un artículo sobre: ${formData.topic}

Palabra clave principal: ${formData.targetKeyword}
${formData.secondaryKeywords ? `Palabras clave secundarias: ${formData.secondaryKeywords}` : ''}
Audiencia: ${formData.audience}
Tono: ${formData.tone}
Extensión deseada: ${formData.wordCount} palabras
${formData.objectives ? `Objetivos: ${formData.objectives}` : ''}
${formData.additionalRequirements ? `Requisitos adicionales: ${formData.additionalRequirements}` : ''}

Por favor, genera los 4 subtemas clave para investigar sobre este tema.`;

      // Usar sessionId prop si existe, sino generar uno nuevo
      const sessionId = propSessionId || generateSessionId();

      // Analyze style if applicable
      let styleAnalysisResult = '';

      // External URLs style analysis
      if (styleMode === 'external' && externalUrls.filter(u => u.trim()).length > 0) {
        try {
          const validUrls = externalUrls.filter(u => u.trim());
          setIsAnalyzingExternal(true);

          const analysisResponse = await webhookService.analyzeStyleFromExternalUrls(validUrls, sessionId, includeStructure);

          if (analysisResponse.styleAnalysis) {
            styleAnalysisResult = analysisResponse.styleAnalysis;
            setAnalysisResultDisplay(styleAnalysisResult);
          }
        } catch (error) {
          // Error analyzing external style
        } finally {
          setIsAnalyzingExternal(false);
        }
      }

      // WordPress style analysis
      if (styleMode === 'wordpress' && selectedWpPosts.length > 0) {
        try {
          // Prepare selected posts
          const selectedPostsData = wpPosts
            .filter(post => selectedWpPosts.includes(post.id.toString()))
            .map(post => ({
              title: post.title,
              content: post.content,
              link: post.link
            }));

          // Para tipos 'standard' y 'news' usa OpenAI GPT-4.1-mini, otros usan n8n
          // projectId: necesario para extraer contenido completo de WordPress
          const analysisResponse = await webhookService.analyzeStyle(selectedPostsData, sessionId, articleType, projectId);

          // Parsing complex webhook response structure
          /*
            Structure example:
            [
              {
                "output": [
                  {
                    "content": [ { "type": "output_text", "text": "..." } ]
                  }
                ]
              }
            ]
          */

          if (Array.isArray(analysisResponse) && analysisResponse.length > 0) {
            const firstItem = analysisResponse[0];

            // Case 1: Nested output array with content array (User provided structure)
            if (firstItem.output && Array.isArray(firstItem.output) && firstItem.output.length > 0) {
              const outputItem = firstItem.output[0];
              if (outputItem.content && Array.isArray(outputItem.content) && outputItem.content.length > 0) {
                styleAnalysisResult = outputItem.content[0].text || JSON.stringify(outputItem.content[0]);
              } else {
                styleAnalysisResult = JSON.stringify(outputItem);
              }
            }
            // Case 2: Direct content property
            else if (firstItem.content) {
              styleAnalysisResult = firstItem.content;
            }
            // Fallback
            else {
              styleAnalysisResult = JSON.stringify(firstItem);
            }
          }
          // Case 3: Standard object response
          else if (analysisResponse.output && typeof analysisResponse.output === 'string') {
            styleAnalysisResult = analysisResponse.output;
          } else if (analysisResponse.message && typeof analysisResponse.message === 'string') {
            styleAnalysisResult = analysisResponse.message;
          } else {
            styleAnalysisResult = JSON.stringify(analysisResponse, null, 2);
          }

          setAnalysisResultDisplay(styleAnalysisResult);

          // Append analysis to prompt
          /* 
          // COMENTADO POR SOLICITUD DEL USUARIO: No enviar el estilo al webhook de subtemas
          if (styleAnalysisResult) {
            prompt += `\n\nIMPORTANTE - ESTILO DE ESCRITURA:
Por favor, asegúrate de que los subtemas y el enfoque del artículo se alineen con este estilo analizado:
${styleAnalysisResult}`;
          }
          */
        } catch (error) {
          // Continue without style analysis rather than breaking the flow
        }
      }

      // Enviar al webhook del chat pidiendo los subtemas
      const chatResponse = await webhookService.sendChatMessage(prompt, sessionId);

      // Extraer subtemas de la respuesta con parsing robusto
      let responseText = '';

      if (Array.isArray(chatResponse) && chatResponse.length > 0) {
        const firstItem = chatResponse[0];
        if (firstItem.output && Array.isArray(firstItem.output) && firstItem.output.length > 0) {
          const outputItem = firstItem.output[0];
          if (outputItem.content && Array.isArray(outputItem.content) && outputItem.content.length > 0) {
            responseText = outputItem.content[0].text || JSON.stringify(outputItem.content[0]);
          } else {
            responseText = JSON.stringify(outputItem);
          }
        } else if (firstItem.content) {
          responseText = firstItem.content;
        } else {
          responseText = JSON.stringify(firstItem);
        }
      } else if (chatResponse.output || chatResponse.message) {
        responseText = chatResponse.output || chatResponse.message;
      } else {
        responseText = JSON.stringify(chatResponse);
      }

      if (typeof responseText !== 'string') {
        responseText = JSON.stringify(responseText);
      }
      const subtopics = extractSubtopics(responseText);

      // Si se seleccionó un tema sugerido, marcarlo como publicado
      if (selectedTopicId) {
        try {
          await webhookService.markTopicAsPublished(selectedTopicId);
        } catch (error) {
          // No bloqueamos el flujo si esto falla
        }
      }

      const rawText = `
Tema: ${formData.topic}
Palabra clave principal: ${formData.targetKeyword}
Palabras clave secundarias: ${formData.secondaryKeywords}
Audiencia objetivo: ${formData.audience}
Tono: ${formData.tone}
Cantidad de palabras: ${formData.wordCount}
Objetivos: ${formData.objectives}
Requisitos adicionales: ${formData.additionalRequirements}
${formData.ctaText ? `CTA: ${formData.ctaText} (${formData.ctaUrl})` : ''}
Modo título: ${formData.isTitleStrict ? 'Título estricto' : 'Usar como tema'}
      `.trim();

      // Prepare style examples if applicable
      let styleExamplePosts: { title: string; content: string; link: string }[] = [];
      if (styleMode === 'wordpress' && selectedWpPosts.length > 0) {
        styleExamplePosts = wpPosts
          .filter(post => selectedWpPosts.includes(post.id.toString()))
          .map(post => ({
            title: post.title,
            content: post.content,
            link: post.link
          }));
      }

      // Enviar datos con subtemas incluidos
      onComplete({
        rawText,
        ...formData,
        subtopics: subtopics.length >= 3 ? subtopics : undefined,
        isResumen: subtopics.length >= 3,
        topic: formData.topic,
        topicId: selectedTopicId, // Pass the selected topic ID
        styleMode,
        styleExamplePosts: styleExamplePosts.length > 0 ? styleExamplePosts : undefined,
        styleAnalysis: styleAnalysisResult,
        sessionId, // Pass session ID
        // Pass new CTA and title mode fields
        ctaText: formData.ctaText || undefined,
        ctaUrl: formData.ctaUrl || undefined,
        isTitleStrict: formData.isTitleStrict
      });
    } catch (error) {
      // Si falla, continuar sin subtemas
      const rawText = `
Tema: ${formData.topic}
Palabra clave principal: ${formData.targetKeyword}
Palabras clave secundarias: ${formData.secondaryKeywords}
Audiencia objetivo: ${formData.audience}
Tono: ${formData.tone}
Cantidad de palabras: ${formData.wordCount}
Objetivos: ${formData.objectives}
Requisitos adicionales: ${formData.additionalRequirements}
${formData.ctaText ? `CTA: ${formData.ctaText} (${formData.ctaUrl})` : ''}
Modo título: ${formData.isTitleStrict ? 'Título estricto' : 'Usar como tema'}
      `.trim();

      onComplete({
        rawText,
        ...formData,
        styleAnalysis: analysisResultDisplay || undefined, // Pass style analysis to next steps
        sessionId: generateSessionId(), // Generate a session ID if one wasn't created (e.g. in catch block fallback if error occurred early)
        // Pass new CTA and title mode fields
        ctaText: formData.ctaText || undefined,
        ctaUrl: formData.ctaUrl || undefined,
        isTitleStrict: formData.isTitleStrict
      });
    } finally {
      setIsGeneratingSubtopics(false);
    }
  };

  const updateField = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const [topics, setTopics] = useState<any[]>([]);
  const [selectedTopicId, setSelectedTopicId] = useState<string>('');
  const [isLoadingTopics, setIsLoadingTopics] = useState(false);

  useEffect(() => {
    const fetchTopics = async () => {
      if (!projectId) {
        return;
      }

      setIsLoadingTopics(true);
      try {
        const data = await webhookService.getTopics(projectId);
        setTopics(data);
      } catch (error) {
        // Error loading topics
      } finally {
        setIsLoadingTopics(false);
      }
    };

    fetchTopics();
  }, [projectId]);

  const [isTopicDropdownOpen, setIsTopicDropdownOpen] = useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsTopicDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleTopicSelect = (topic: any) => {
    setSelectedTopicId(topic.id.toString());
    updateField('topic', topic.titulo);
    setIsTopicDropdownOpen(false);
  };

  const handleDeleteTopic = async (topicId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Evitar seleccionar el tema al borrarlo
    if (!confirm('¿Estás seguro de que quieres eliminar este tema sugerido?')) return;

    try {
      await webhookService.deleteTopic(topicId);
      // Actualizar estado local eliminando el tema
      setTopics(prev => prev.filter(t => t.id.toString() !== topicId.toString()));

      // Si el tema eliminado estaba seleccionado, limpiar selección
      if (selectedTopicId === topicId.toString()) {
        setSelectedTopicId('');
        updateField('topic', '');
      }
    } catch (error) {
      alert('Error al eliminar el tema');
    }
  };

  // WordPress Style Logic
  const fetchWpPosts = async () => {
    if (!projectId) return;
    setIsLoadingWpPosts(true);
    try {
      const posts = await projectService.getWordPressPosts(projectId);
      setWpPosts(posts);
      // Auto-expand first post if available
      if (posts.length > 0) {
        setExpandedPostId(posts[0].id.toString());
      }
    } catch (error) {
      // Error fetching WP posts
    } finally {
      setIsLoadingWpPosts(false);
    }
  };

  const handleWpPostToggle = (postId: string) => {
    setSelectedWpPosts(prev => {
      const isSelected = prev.includes(postId);
      if (isSelected) {
        return prev.filter(id => id !== postId);
      } else {
        if (prev.length >= 3) {
          alert('Máximo 3 artículos permitidos');
          return prev;
        }
        return [...prev, postId];
      }
    });
  };

  // Trigger fetch when switching to wordpress mode
  useEffect(() => {
    if (styleMode === 'wordpress' && wpPosts.length === 0) {
      fetchWpPosts();
    }
  }, [styleMode, projectId]);


  const selectedTopic = topics.find(t => t.id.toString() === selectedTopicId);

  const toneOptions = [
    { value: 'profesional', label: 'Profesional', icon: Briefcase, desc: 'Formal y experto' },
    { value: 'casual', label: 'Casual', icon: Smile, desc: 'Cercano y relajado' },
    { value: 'técnico', label: 'Técnico', icon: Zap, desc: 'Preciso y detallado' },
    { value: 'educativo', label: 'Educativo', icon: BookOpen, desc: 'Didáctico y claro' },
  ];

  if (isGeneratingSubtopics) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-6">
        <Loading size="lg" text="Creando estructura inteligente..." />
        {analysisResultDisplay && (
          <div className="max-w-xl w-full bg-purple-50 rounded-xl p-6 border border-purple-100 animate-in fade-in slide-in-from-bottom-4 duration-700">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-5 h-5 text-purple-600" />
              <h3 className="font-semibold text-purple-900">Estilo Detectado</h3>
            </div>
            <StyleAnalysisCard data={analysisResultDisplay} />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-6">
      <div className="mb-10 flex items-center justify-between">
        <div>

          <h2 className="text-4xl font-extrabold text-gray-900 mb-3 tracking-tight">Configuración de Contenido</h2>
          <p className="text-lg text-gray-600 max-w-2xl">Define los parámetros clave para generar un artículo de alta calidad</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Sección 1: Tema y Palabras Clave */}
        <Card className="relative z-20 border-0 shadow-xl ring-1 ring-gray-200/50 bg-white rounded-2xl">
          <div className="rounded-t-2xl border-b border-gray-100 bg-gradient-to-r from-blue-50/50 to-indigo-50/50 p-6">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-blue-600 rounded-xl shadow-lg shadow-blue-200">
                <Target className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">Información Principal</h3>
                <p className="text-sm text-gray-500 font-medium">¿Sobre qué quieres escribir hoy?</p>
              </div>
            </div>
          </div>

          <div className="p-8 space-y-8">
            {/* Selector de Temas Custom */}
            <div className="space-y-3 relative" ref={dropdownRef}>
              <label className="block text-sm font-bold text-gray-900">
                Seleccionar tema sugerido <span className="text-gray-400 font-normal ml-1">(Opcional)</span>
              </label>

              <div className="relative">
                <button
                  type="button"
                  onClick={() => !isLoadingTopics && setIsTopicDropdownOpen(!isTopicDropdownOpen)}
                  className={`w-full text-left pl-4 pr-10 py-4 rounded-xl border transition-all duration-200 flex items-center justify-between group ${isTopicDropdownOpen
                    ? 'border-blue-500 ring-4 ring-blue-500/10 bg-white shadow-lg'
                    : 'border-gray-200 bg-gray-50/50 hover:bg-white hover:border-blue-300 hover:shadow-md'
                    }`}
                  disabled={isLoadingTopics}
                >
                  <span className={`block truncate font-medium ${selectedTopic ? 'text-gray-900' : 'text-gray-500'}`}>
                    {selectedTopic ? selectedTopic.titulo : '-- Explorar temas disponibles --'}
                  </span>
                  <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform duration-300 ${isTopicDropdownOpen ? 'rotate-180 text-blue-500' : 'group-hover:text-blue-500'}`} />
                </button>

                {/* Dropdown Menu */}
                {isTopicDropdownOpen && (
                  <div className="absolute z-50 mt-2 w-full bg-white rounded-xl shadow-2xl border border-gray-100 max-h-96 overflow-y-auto overflow-x-hidden animate-in fade-in zoom-in-95 duration-100 scrollbar-thin scrollbar-thumb-gray-200 scrollbar-track-transparent">
                    <div className="p-2 space-y-1">
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedTopicId('');
                          updateField('topic', ''); // Clear the topic field as well
                          setIsTopicDropdownOpen(false);
                        }}
                        className="w-full text-left px-4 py-3 rounded-lg hover:bg-gray-50 text-gray-500 font-medium transition-colors text-sm"
                      >
                        -- Ninguno --
                      </button>
                      {topics.map((topic) => (
                        <div
                          key={topic.id}
                          className={`w-full relative rounded-lg transition-all duration-200 group flex ${selectedTopicId === topic.id.toString() ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                        >
                          <button
                            type="button"
                            onClick={() => handleTopicSelect(topic)}
                            className="flex-1 text-left px-4 py-3"
                          >
                            <div className="flex items-start justify-between pr-8">
                              <span className={`font-semibold block mb-0.5 transition-colors ${selectedTopicId === topic.id.toString() ? 'text-blue-700' : 'text-gray-700 group-hover:text-blue-600'}`}>
                                {topic.titulo}
                              </span>
                            </div>
                            {topic.descripcion && (
                              <span className={`text-xs block line-clamp-2 ${selectedTopicId === topic.id.toString() ? 'text-blue-500/80' : 'text-gray-400 group-hover:text-gray-500'}`}>
                                {topic.descripcion}
                              </span>
                            )}
                          </button>

                          <div className="absolute right-2 top-3 flex items-start gap-1">
                            {selectedTopicId === topic.id.toString() && (
                              <Check className="w-4 h-4 text-blue-600 mt-1 mr-1" />
                            )}
                            <button
                              type="button"
                              onClick={(e) => handleDeleteTopic(topic.id, e)}
                              className="p-1 rounded-md text-gray-300 hover:text-red-500 hover:bg-red-50 transition-all opacity-0 group-hover:opacity-100"
                              title="Eliminar tema"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {isLoadingTopics && (
                <div className="flex items-center space-x-2 text-sm text-blue-600 animate-pulse font-medium">
                  <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                  <span>Cargando sugerencias...</span>
                </div>
              )}

              {selectedTopic?.descripcion && !isTopicDropdownOpen && (
                <div className="mt-4 p-5 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-100 flex items-start space-x-4 animate-fade-in shadow-sm">
                  <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0">
                    <Sparkles className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-blue-900 mb-1">Sobre este tema</h4>
                    <p className="text-sm text-blue-700/90 leading-relaxed">{selectedTopic.descripcion}</p>
                  </div>
                </div>
              )}
            </div>

            <div className="relative py-4">
              <div className="absolute inset-0 flex items-center" aria-hidden="true">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center">
                <span className="px-4 bg-white text-sm text-gray-400 font-semibold tracking-wide uppercase">O personaliza tu tema</span>
              </div>
            </div>

            <div className="grid gap-8">
              <div className="space-y-3">
                <Input
                  label="Tema del contenido"
                  placeholder="Ej: Estrategias de marketing digital para 2025"
                  value={formData.topic}
                  onChange={(e) => updateField('topic', e.target.value)}
                  error={errors.topic}
                  icon={<MessageCircle className="w-5 h-5" />}
                  className="text-lg"
                />

                {/* Checkbox: Título Estricto - Positioned right below title input */}
                <label className="flex items-start gap-3 px-1 cursor-pointer group">
                  <div className="flex items-center h-6">
                    <input
                      type="checkbox"
                      checked={formData.isTitleStrict}
                      onChange={(e) => updateField('isTitleStrict', e.target.checked)}
                      className="w-4 h-4 text-blue-600 bg-white border-2 border-gray-300 rounded focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 cursor-pointer transition-all"
                    />
                  </div>
                  <div className="flex-1">
                    <span className="text-sm font-semibold text-gray-700 group-hover:text-blue-600 transition-colors">
                      Usar como título estricto
                    </span>
                    <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
                      {formData.isTitleStrict
                        ? 'El título se usará literalmente como está escrito arriba'
                        : 'El tema se usará como referencia y la IA generará el título final'
                      }
                    </p>
                  </div>
                </label>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                <Input
                  label="Palabra clave principal"
                  placeholder="Ej: marketing digital"
                  value={formData.targetKeyword}
                  onChange={(e) => updateField('targetKeyword', e.target.value)}
                  error={errors.targetKeyword}
                  icon={<Hash className="w-4 h-4" />}
                  helperText="La palabra clave principal para SEO"
                />

                <Input
                  label="Palabras clave secundarias"
                  placeholder="Ej: SEO, redes sociales, branding"
                  value={formData.secondaryKeywords}
                  onChange={(e) => updateField('secondaryKeywords', e.target.value)}
                  icon={<Hash className="w-4 h-4" />}
                  helperText="Separadas por comas"
                />
              </div>
            </div>
          </div>
        </Card>

        {/* Sección CTA (Call To Action) */}
        <Card className="border-0 shadow-xl ring-1 ring-gray-200/50 bg-white rounded-2xl">
          <div className="rounded-t-2xl border-b border-gray-100 bg-gradient-to-r from-green-50/50 to-emerald-50/50 p-6">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-green-600 rounded-xl shadow-lg shadow-green-200">
                <ExternalLink className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">
                  Call To Action (CTA)
                  <span className="ml-2 text-xs font-normal text-gray-500 bg-gray-100 px-2 py-1 rounded">Opcional</span>
                </h3>
                <p className="text-sm text-gray-500 font-medium">Enlace estratégico al final del artículo</p>
              </div>
            </div>
          </div>

          <div className="p-8 space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <Input
                label="Texto del CTA"
                placeholder="Ej: Descubre más información aquí"
                value={formData.ctaText}
                onChange={(e) => updateField('ctaText', e.target.value)}
                helperText="El texto que aparecerá como enlace en la conclusión"
              />

              <Input
                label="URL del CTA"
                placeholder="https://..."
                value={formData.ctaUrl}
                onChange={(e) => updateField('ctaUrl', e.target.value)}
                icon={<ExternalLink className="w-4 h-4" />}
                helperText="Destino del enlace del CTA"
              />
            </div>

            {formData.ctaText && formData.ctaUrl && (
              <div className="mt-4 p-4 bg-green-50 rounded-xl border border-green-100 flex items-start space-x-3">
                <div className="p-2 bg-green-100 rounded-lg flex-shrink-0">
                  <Sparkles className="w-4 h-4 text-green-600" />
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-bold text-green-900 mb-1">Vista previa del CTA</h4>
                  <p className="text-sm text-green-700">
                    Al final del artículo se insertará: <a href={formData.ctaUrl} className="underline font-medium" target="_blank" rel="noopener noreferrer">{formData.ctaText}</a>
                  </p>
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* Sección 2: Audiencia y Estilo */}
        <Card className="overflow-hidden border-0 shadow-xl ring-1 ring-gray-100 bg-white">
          <div className="border-b border-gray-100 bg-gray-50/50 p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Users className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Audiencia y Estilo</h3>
                  <p className="text-sm text-gray-500">Define a quién le hablas y cómo</p>
                </div>
              </div>
            </div>
          </div>

          <div className="p-8 space-y-8">
            <Input
              label="Audiencia objetivo"
              placeholder="Ej: Emprendedores y dueños de PYMES"
              value={formData.audience}
              onChange={(e) => updateField('audience', e.target.value)}
              error={errors.audience}
              icon={<Users className="w-4 h-4" />}
            />

            <div>
              <div className="flex items-center justify-between mb-4">
                <label className="block text-sm font-medium text-gray-700">
                  Tono del contenido
                </label>

                {/* Tabs Selector */}
                <div className="bg-gray-100 p-1 rounded-lg flex text-xs font-semibold">
                  <button
                    type="button"
                    onClick={() => setStyleMode('preset')}
                    className={`px-3 py-1.5 rounded-md transition-all ${styleMode === 'preset'
                      ? 'bg-white text-purple-600 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                      }`}
                  >
                    Estilos Predefinidos
                  </button>
                  <button
                    type="button"
                    onClick={() => setStyleMode('wordpress')}
                    className={`px-3 py-1.5 rounded-md transition-all ${styleMode === 'wordpress'
                      ? 'bg-white text-purple-600 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                      }`}
                  >
                    Clonar WordPress
                  </button>
                  <button
                    type="button"
                    onClick={() => setStyleMode('external')}
                    className={`px-3 py-1.5 rounded-md transition-all flex items-center gap-1 ${styleMode === 'external'
                      ? 'bg-white text-purple-600 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                      }`}
                  >
                    <Globe className="w-3 h-3" />
                    URL Externa
                  </button>
                </div>
              </div>

              {styleMode === 'preset' ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 animate-in fade-in slide-in-from-top-2 duration-300">
                  {toneOptions.map((option) => {
                    const Icon = option.icon;
                    const isSelected = formData.tone === option.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => updateField('tone', option.value)}
                        className={`relative p-4 rounded-xl border-2 text-left transition-all duration-200 hover:shadow-md ${isSelected
                          ? 'border-purple-500 bg-purple-50 ring-1 ring-purple-500'
                          : 'border-gray-100 bg-white hover:border-purple-200 hover:bg-gray-50'
                          }`}
                      >
                        {isSelected && (
                          <div className="absolute top-3 right-3">
                            <div className="w-5 h-5 bg-purple-500 rounded-full flex items-center justify-center">
                              <Check className="w-3 h-3 text-white" />
                            </div>
                          </div>
                        )}
                        <div className={`p-2 rounded-lg w-fit mb-3 ${isSelected ? 'bg-purple-200' : 'bg-gray-100'}`}>
                          <Icon className={`w-5 h-5 ${isSelected ? 'text-purple-700' : 'text-gray-500'}`} />
                        </div>
                        <div className="font-semibold text-gray-900">{option.label}</div>
                        <div className="text-xs text-gray-500 mt-1">{option.desc}</div>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="space-y-4 animate-in fade-in slide-in-from-top-2 duration-300">
                  <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 flex items-start gap-3">
                    <div className="p-2 bg-white rounded-full shadow-sm">
                      <Sparkles className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-blue-900">Análisis de Estilo Inteligente</h4>
                      <p className="text-xs text-blue-700 mt-1">
                        Selecciona hasta 3 artículos de tu WordPress. La IA analizará su tono, estructura y vocabulario para replicar tu estilo único.
                      </p>
                    </div>
                  </div>

                  {isLoadingWpPosts ? (
                    <div className="p-8 text-center bg-gray-50 rounded-xl border border-dashed border-gray-200">
                      <Loading size="md" text="Conectando con tu WordPress..." />
                    </div>
                  ) : wpPosts.length === 0 ? (
                    <div className="p-8 text-center bg-gray-50 rounded-xl border border-dashed border-gray-200">
                      <p className="text-gray-500 text-sm mb-3">No pudimos cargar tus artículos automáticamente.</p>
                      <Button onClick={fetchWpPosts} variant="outline" size="sm">reintentar conexión</Button>
                    </div>
                  ) : (
                    <div className="border border-gray-200 rounded-xl divide-y divide-gray-100 overflow-hidden bg-white max-h-[400px] overflow-y-auto scrollbar-thin">
                      {wpPosts.map((post) => (
                        <div key={post.id} className={`transition-colors ${selectedWpPosts.includes(post.id.toString()) ? 'bg-purple-50/50' : 'hover:bg-gray-50'}`}>
                          <div className="p-4 flex items-start gap-3">
                            <input
                              type="checkbox"
                              checked={selectedWpPosts.includes(post.id.toString())}
                              onChange={() => handleWpPostToggle(post.id.toString())}
                              className="mt-1 w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500 cursor-pointer"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between mb-1">
                                <h4
                                  className="text-sm font-medium text-gray-900 truncate cursor-pointer hover:text-purple-600"
                                  onClick={() => setExpandedPostId(expandedPostId === post.id.toString() ? null : post.id.toString())}
                                >
                                  {post.title}
                                </h4>
                                <span className="text-[10px] text-gray-400 whitespace-nowrap ml-2">
                                  {new Date(post.date).toLocaleDateString()}
                                </span>
                              </div>

                              <button
                                type="button"
                                onClick={() => setExpandedPostId(expandedPostId === post.id.toString() ? null : post.id.toString())}
                                className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
                              >
                                {expandedPostId === post.id.toString() ? (
                                  <>Ocultar vista previa <ChevronDown className="w-3 h-3 rotate-180" /></>
                                ) : (
                                  <>Ver vista previa <ChevronDown className="w-3 h-3" /></>
                                )}
                              </button>

                              {expandedPostId === post.id.toString() && (
                                <div className="mt-3 p-3 bg-gray-50 rounded-lg text-xs text-gray-600 border border-gray-100">
                                  <div
                                    className="prose prose-xs max-w-none line-clamp-6"
                                    dangerouslySetInnerHTML={{ __html: post.content }}
                                  />
                                  <a href={post.link} target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:text-purple-700 mt-2 inline-block font-medium">
                                    Ver artículo completo →
                                  </a>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="flex justify-between items-center text-xs text-gray-500 px-1">
                    <span>{selectedWpPosts.length} de 3 seleccionados</span>
                    {selectedWpPosts.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setSelectedWpPosts([])}
                        className="text-red-500 hover:text-red-600 font-medium"
                      >
                        Limpiar selección
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* External URLs Mode */}
              {styleMode === 'external' && (
                <div className="space-y-4 animate-in fade-in slide-in-from-top-2 duration-300">
                  <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-4 flex items-start gap-3">
                    <div className="p-2 bg-white rounded-full shadow-sm">
                      <Globe className="w-4 h-4 text-emerald-600" />
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-emerald-900">Análisis de Estilo desde URLs Externas</h4>
                      <p className="text-xs text-emerald-700 mt-1">
                        Añade URLs de artículos externos (no de tu WordPress). La IA extraerá el contenido y analizará su estilo para replicarlo.
                      </p>
                    </div>
                  </div>

                  {/* Checkbox para incluir estructura */}
                  <label className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg cursor-pointer hover:bg-amber-100 transition-colors">
                    <input
                      type="checkbox"
                      checked={includeStructure}
                      onChange={(e) => setIncludeStructure(e.target.checked)}
                      className="w-4 h-4 text-amber-600 border-amber-300 rounded focus:ring-amber-500"
                    />
                    <div>
                      <span className="text-sm font-medium text-amber-800">
                        Incluir análisis de ESTRUCTURA
                      </span>
                      <p className="text-xs text-amber-600 mt-0.5">
                        Extrae la arquitectura exacta del artículo (headings, secciones, FAQs) para replicar el formato con Gemini
                      </p>
                    </div>
                  </label>

                  <div className="space-y-3">
                    {externalUrls.map((url, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <div className="flex-1 relative">
                          <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <input
                            type="url"
                            value={url}
                            onChange={(e) => {
                              const newUrls = [...externalUrls];
                              newUrls[index] = e.target.value;
                              setExternalUrls(newUrls);
                            }}
                            placeholder="https://ejemplo.com/articulo"
                            className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-all text-sm"
                          />
                        </div>
                        {externalUrls.length > 1 && (
                          <button
                            type="button"
                            onClick={() => {
                              const newUrls = externalUrls.filter((_, i) => i !== index);
                              setExternalUrls(newUrls);
                            }}
                            className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>

                  {externalUrls.length < 5 && (
                    <button
                      type="button"
                      onClick={() => setExternalUrls([...externalUrls, ''])}
                      className="flex items-center gap-2 text-sm text-emerald-600 hover:text-emerald-700 font-medium px-3 py-2 rounded-lg hover:bg-emerald-50 transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                      Añadir otra URL
                    </button>
                  )}

                  <div className="flex justify-between items-center text-xs text-gray-500 px-1">
                    <span>{externalUrls.filter(u => u.trim()).length} de 5 URLs máximo</span>
                    {externalUrls.filter(u => u.trim()).length > 0 && (
                      <button
                        type="button"
                        onClick={() => setExternalUrls([''])}
                        className="text-red-500 hover:text-red-600 font-medium"
                      >
                        Limpiar URLs
                      </button>
                    )}
                  </div>

                  {isAnalyzingExternal && (
                    <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-100 flex items-center gap-3">
                      <div className="animate-spin w-5 h-5 border-2 border-emerald-600 border-t-transparent rounded-full" />
                      <span className="text-sm text-emerald-700 font-medium">Extrayendo y analizando contenido...</span>
                    </div>
                  )}
                </div>
              )}
            </div>


            <div className="bg-gray-50 rounded-xl p-6 border border-gray-100">
              <div className="flex justify-between items-center mb-4">
                <label className="text-sm font-medium text-gray-700">
                  Extensión aproximada
                </label>
                <span className="px-3 py-1 bg-white rounded-lg shadow-sm border border-gray-200 text-sm font-bold text-purple-600">
                  {formData.wordCount} palabras
                </span>
              </div>
              <input
                type="range"
                min="500"
                max="5000"
                step="100"
                value={formData.wordCount}
                onChange={(e) => updateField('wordCount', parseInt(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-2 font-medium">
                <span>500 (Breve)</span>
                <span>2,500 (Estándar)</span>
                <span>5,000 (Extenso)</span>
              </div>
            </div>
          </div>
        </Card >

        {/* Sección 3: Detalles Adicionales */}
        < Card className="overflow-hidden border-0 shadow-xl ring-1 ring-gray-100 bg-white" >
          <div className="border-b border-gray-100 bg-gray-50/50 p-6">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Sparkles className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Detalles Finales</h3>
                <p className="text-sm text-gray-500">Instrucciones específicas para la IA</p>
              </div>
            </div>
          </div>

          <div className="p-8 space-y-6">
            <Textarea
              label="Objetivos del contenido"
              placeholder="Ej: Educar a la audiencia sobre estrategias de marketing digital, generar leads, posicionar como expertos..."
              value={formData.objectives}
              onChange={(e) => updateField('objectives', e.target.value)}
              rows={3}
              className="bg-white border-gray-300 shadow-sm rounded-xl"
            />

            <Textarea
              label="Requisitos adicionales (opcional)"
              placeholder="Ej: Incluir ejemplos prácticos, mencionar herramientas específicas, agregar estadísticas recientes..."
              value={formData.additionalRequirements}
              onChange={(e) => updateField('additionalRequirements', e.target.value)}
              rows={3}
              className="bg-white border-gray-300 shadow-sm rounded-xl"
            />
          </div>
        </Card >

        {/* Botón de Acción */}
        < div className="sticky bottom-6 z-10" >
          <div className="absolute inset-0 bg-white/80 backdrop-blur-md -m-6 rounded-t-2xl border-t border-gray-100 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]" />
          <div className="relative flex justify-end pt-2">
            <Button
              type="submit"
              size="lg"
              disabled={isGeneratingSubtopics}
              className="w-full md:w-auto bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-0.5 px-8 py-4 text-lg rounded-xl"
              icon={<Sparkles className="w-5 h-5" />}
            >
              {isGeneratingSubtopics ? 'Analizando y Estructurando...' : 'Generar Artículo Completo'}
            </Button>
          </div>
        </div >
      </form >

      {/* Loading overlay */}
      {
        isGeneratingSubtopics && (
          <div className="fixed inset-0 bg-white/90 backdrop-blur-sm flex items-center justify-center z-50 animate-fade-in">
            <div className="text-center max-w-md p-8 bg-white rounded-2xl shadow-2xl border border-gray-100">
              <Loading size="lg" text="Analizando tu briefing..." />
              <h3 className="text-xl font-bold text-gray-900 mt-6 mb-2">Creando estructura inteligente</h3>
              <p className="text-gray-500">
                Estamos identificando los subtemas más relevantes para tu artículo basándonos en tu configuración.
              </p>
            </div>
          </div>
        )
      }
    </div >
  );
};









